import { useState, useEffect } from 'react'
import {
    IconButton, Avatar, Box, CloseButton, Flex, HStack, VStack, Icon, Text,
    Drawer, useDisclosure, BoxProps, Menu, Button,
    useBreakpointValue, Link as CLink, Portal, Separator,
} from '@chakra-ui/react'
import { toaster } from './ui/toaster'
import { useColorModeValue } from './ui/color-mode'
import {
    FiHome, FiCalendar, FiList, FiChevronDown, FiCopy, FiLayout,
    FiStar, FiHeart, FiUser, FiSettings, /* HIDDEN_TODO #8 FiMonitor, */ FiClock, FiBell, /* HIDDEN_TODO #22 FiTrendingUp, */
    /* HIDDEN_TODO #20/#21 FiCheck, */ FiGrid, FiUsers, FiShield, FiGift,
} from 'react-icons/fi'
import { LuMenu, LuX } from 'react-icons/lu'
import { IconType } from 'react-icons'
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom'
import {
    isBarberLoggedIn, isCustomerLoggedIn,
    barberLogout, customerLogout, UserType,
} from '../slices/authSlice.ts'
import { useAppDispatch, useAppSelector } from '../hooks/store.ts'
import { useDialogBodyCleanup } from '../hooks/useDialogBodyCleanup'
import { useSideMenuVisibility } from '../hooks/useSideMenuVisiblity.ts'
import { useGetCurrentUserQuery } from '../services/userService.ts'
import { analytics } from '../utils/analytics.ts'
import { useGetCustomerMeQuery } from '../services/customerPortalService.ts'
import { Role, hasAdminRole } from '../types'
import { useTranslation } from 'react-i18next'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { GiPalmTree } from 'react-icons/gi'
import { IMAGE_URL } from '../constants/API.ts'
import { SubscriptionBadge } from './Subscription/SubscriptionBadge.tsx'
import ColorModeToggle from './ColorModeToggle.tsx'
import { useBarberPageHeaderValue } from '../contexts/BarberPageHeader.tsx'

// ─── Design tokens (Barbermate-6 chrome — light-only) ──────────
const BLUE = '#0088FF'
const BLUE_GLOW = '0 4px 12px rgba(0,136,255,.30)'
const RED = '#FF3D3D'
const MONO = "'Space Mono', monospace"
const HEADING = "'Montserrat', sans-serif"
const N: Record<number, string> = {
    50: '#FAFAFA', 100: '#F5F5F5', 200: '#E5E5E5', 300: '#D4D4D4',
    400: '#A3A3A3', 500: '#737373', 600: '#525252', 700: '#404040',
    800: '#262626', 900: '#171717',
}

enum LinkType { Copy = 'copy' }

interface LinkItemProps { name: string; icon: IconType; type?: LinkType; path: string }
interface SidebarProps extends BoxProps { user: any; onClose: () => void; activePath: string }

// ─── Route → topbar title map ─────────────────────────────────────
const TOPBAR_TITLE_BY_PATH: ReadonlyArray<readonly [string, string]> = [
    ['/dashboard', 'navbar.dashboard'],
    ['/barber/plan-of-day', 'navbar.planOfDay'],
    ['/barber/insights', 'navbar.insights'],
    ['/barber/kiosk', 'navbar.kiosk'],
    ['/barber/calendar', 'navbar.calendar'],
    ['/barber/reservations', 'navbar.reservations'],
    ['/barber/customers', 'navbar.customers'],
    ['/barber/vacations', 'navbar.vacations'],
    ['/barber/waitlist', 'waitlist.navbar'],
    ['/barber/notifications', 'notifications.title'],
    ['/barber/reviews', 'navbar.reviews'],
    ['/barber/gift-cards', 'navbar.giftCards'],
    ['/barber/settings', 'navbar.settings'],
    ['/admin', 'navbar.admin'],
]
const topbarTitleKeyFor = (pathname: string) =>
    TOPBAR_TITLE_BY_PATH.find(([p]) => pathname.startsWith(p))?.[1] || 'navbar.dashboard'

// ─── Sidebar (barber dashboard) — Barbermate-6 style ─────────────
const SidebarContent = ({ user, onClose, activePath, ...rest }: SidebarProps) => {
    const { t } = useTranslation()
    const slug = user?.barber_shop?.slug || ''
    const bookingLink = slug ? `/r/${slug}` : '/'

    const chromeBg = 'white'
    const chromeBorder = N[200]
    const wordmarkColor = N[900]
    const closeColor = N[600]

    const isAdmin = hasAdminRole(user?.roles)
    const items: Array<LinkItemProps> = [
        { name: 'navbar.home', icon: FiHome, path: '/dashboard' },
        { name: 'navbar.planOfDay', icon: FiGrid, path: '/barber/plan-of-day' },
        // TODO HIDDEN_TODO #8 — Kiosk page is a placeholder, not yet implemented. See HIDDEN_TODO.md #8.
        // { name: 'navbar.kiosk', icon: FiMonitor, path: '/barber/kiosk' },
        { name: 'navbar.calendar', icon: FiCalendar, path: '/barber/calendar' },
        { name: 'navbar.reservations', icon: FiList, path: '/barber/reservations' },
        { name: 'waitlist.navbar', icon: FiClock, path: '/barber/waitlist' },
        { name: 'navbar.customers', icon: FiUsers, path: '/barber/customers' },
        { name: 'navbar.vacations', icon: GiPalmTree, path: '/barber/vacations' },
        { name: 'navbar.reviews', icon: FiStar, path: '/barber/reviews' },
        { name: 'navbar.giftCards', icon: FiGift, path: '/barber/gift-cards' },
        { name: 'notifications.title', icon: FiBell, path: '/barber/notifications' },
        // TODO HIDDEN_TODO #22 — Insights page uses mock data only. See HIDDEN_TODO.md #22.
        // { name: 'navbar.insights', icon: FiTrendingUp, path: '/barber/insights' },
        { name: 'navbar.settings', icon: FiSettings, path: '/barber/settings' },
        ...(isAdmin ? [{ name: 'navbar.admin', icon: FiShield, path: '/admin' }] : []),
    ]
    const handleCopy = (text: string) => {
        navigator.clipboard.writeText(`${window.location.origin}${text}`)
        toaster.create({
            title: t('navbar.linkCopied'),
            description: t('navbar.linkCopiedDescription'),
            type: 'success', duration: 2000, closable: true,
        })
    }

    return (
        <Box
            pos="fixed" top={0} left={0} h="full" w={{ base: 'full', md: 60 }}
            bg={chromeBg} borderRight="1px solid" borderRightColor={chromeBorder}
            zIndex={50} display="flex" flexDirection="column" {...rest}
        >
            {/* 80px header w/ wordmark */}
            <Flex h="80px" align="center" px="30px" justify="space-between">
                <CLink asChild
                    fontFamily={HEADING} fontWeight={700} fontSize="22px"
                    letterSpacing="-0.04em" color={wordmarkColor}
                    _hover={{ textDecor: 'none' }}
                >
                    <RouterLink to="/">BARBER<Text as="span" color={RED}>MATE</Text></RouterLink>
                </CLink>
                <CloseButton display={{ base: 'flex', md: 'none' }} onClick={onClose} color={closeColor} />
            </Flex>
            {/* Nav */}
            <Box as="nav" display="flex" flexDirection="column" gap="3px" pt="6px" flex="1" overflowY="auto">
                {items.map(it => (
                    <SideNavItem key={it.path}
                        icon={it.icon}
                        path={it.path}
                        active={activePath.startsWith(it.path) && it.path !== '/dashboard'
                            || (it.path === '/dashboard' && activePath === '/dashboard')}
                        onClick={onClose}
                    >{t(it.name)}</SideNavItem>
                ))}
                <Box mx="14px" mt="18px" pt="18px" borderTop="1px solid" borderTopColor={chromeBorder}
                    display="flex" flexDirection="column" gap="3px">
                    <SideNavItem icon={FiLayout} path={bookingLink} onClick={onClose} flat>
                        {t('navbar.myBookingPage')}
                    </SideNavItem>
                    <CopyToClipboard text={`${window.location.origin}${bookingLink}`}
                        onCopy={() => handleCopy(bookingLink)}>
                        <SideNavItem icon={FiCopy} path="#" onClick={onClose} flat>
                            {t('navbar.copyBookingLink')}
                        </SideNavItem>
                    </CopyToClipboard>
                </Box>
            </Box>
        </Box>
    )
}

const SideNavItem = ({ icon, children, path, active, flat, onClick }: {
    icon: IconType; children: string; path: string; active?: boolean; flat?: boolean; onClick?: () => void
}) => {
    const inactiveColor = N[600]
    const hoverBg = N[100]
    const hoverColor = N[800]
    return (
        <RouterLink to={path} style={{ textDecoration: 'none' }} onClick={onClick}>
            <Flex
                align="center" gap="14px"
                mx="14px" px="16px" py="11px"
                borderRadius="12px"
                fontSize="14px"
                fontWeight={active ? 600 : 500}
                color={active ? 'white' : inactiveColor}
                bg={active ? BLUE : 'transparent'}
                boxShadow={active ? BLUE_GLOW : 'none'}
                transition="background .14s, color .14s"
                _hover={!active ? { bg: hoverBg, color: hoverColor } : undefined}
                opacity={flat && !active ? 1 : 1}
            >
                <Icon as={icon} fontSize="18px" />
                {children}
            </Flex>
        </RouterLink>
    )
}

// ─── LangToggle (used in dropdown only on barber pages) ──────────
const LangToggle = () => {
    const { i18n } = useTranslation()
    const current = (['en', 'sk'] as const).find((l) => i18n.language?.startsWith(l)) ?? 'en'
    return (
        <HStack gap={0} border="1px solid" borderColor={N[200]} borderRadius="full" overflow="hidden">
            {(['en', 'sk'] as const).map((lng) => {
                const active = lng === current
                return (
                    <Box key={lng} as="button" onClick={() => i18n.changeLanguage(lng)}
                        px={3} py={1.5} fontFamily={MONO} fontSize="10px"
                        letterSpacing="0.2em" fontWeight={700}
                        bg={active ? BLUE : 'transparent'}
                        color={active ? 'white' : N[700]}
                        transition="all .15s"
                    >{lng.toUpperCase()}</Box>
                )
            })}
        </HStack>
    )
}

const getNavLinkStyle = (textColor: string) => ({
    fontFamily: MONO, fontSize: '11px', letterSpacing: '0.2em',
    color: textColor, _hover: { color: BLUE }, textDecoration: 'none',
})

// ═══════════════════════════════════════════════════════════════
// Role / shop helpers
// ═══════════════════════════════════════════════════════════════

export const resolveActiveRole = (pathname: string, isBarberLogged: boolean, isCustomerLogged: boolean): UserType => {
    if (pathname.startsWith('/customer/') && isCustomerLogged) return 'customer'
    if (pathname.startsWith('/barber/') || pathname === '/dashboard') {
        if (isBarberLogged) return 'barber'
    }
    if (isBarberLogged) return 'barber'
    if (isCustomerLogged) return 'customer'
    return null
}

// ═══════════════════════════════════════════════════════════════
// PUBLIC / CUSTOMER TOP BAR (existing flow — unchanged visuals)
// ═══════════════════════════════════════════════════════════════

const PublicTopBar = ({ barberUser, customerUser }: { barberUser: any; customerUser: any }) => {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const location = useLocation()
    const { open: drawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure()
    useDialogBodyCleanup(drawerOpen)
    const [scrolled, setScrolled] = useState(false)
    const isMobile = useBreakpointValue({ base: true, lg: false })

    const isBarberLogged = useAppSelector(isBarberLoggedIn)
    const isCustomerLogged = useAppSelector(isCustomerLoggedIn)
    const isLogged = isBarberLogged || isCustomerLogged
    const activeRole: UserType = resolveActiveRole(location.pathname, isBarberLogged, isCustomerLogged)
    const hasBothRoles = isBarberLogged && isCustomerLogged

    const isLandingPage = location.pathname === '/'
    const isBookingPage = location.pathname.startsWith('/r/')

    const textColor = useColorModeValue('gray.700', 'gray.200')
    const logoColor = useColorModeValue('gray.900', 'white')
    const navBgSolid = useColorModeValue('rgba(255,255,255,0.95)', 'rgba(26,32,44,0.95)')
    const navBorderColor = useColorModeValue('gray.200', 'gray.700')
    const drawerBg = useColorModeValue('gray.50', 'gray.800')
    const menuBg = useColorModeValue('white', 'gray.800')
    const menuBorder = useColorModeValue('gray.200', 'gray.600')
    const navLinkStyle = getNavLinkStyle(textColor)

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12)
        window.addEventListener('scroll', onScroll)
        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    const signOutActive = () => {
        if (activeRole === 'barber') {
            analytics.logout('barber'); analytics.clearUser(); dispatch(barberLogout())
            if (!isCustomerLogged) navigate('/'); else navigate('/customer/portal')
        } else if (activeRole === 'customer') {
            analytics.logout('customer'); analytics.clearUser(); dispatch(customerLogout())
            if (!isBarberLogged) navigate('/'); else navigate('/dashboard')
        }
    }
    const signOutOther = () => {
        if (activeRole === 'barber' && isCustomerLogged) {
            analytics.logout('customer'); dispatch(customerLogout())
        } else if (activeRole === 'customer' && isBarberLogged) {
            analytics.logout('barber'); dispatch(barberLogout())
        }
    }

    const barberName = barberUser?.visible_name
        || `${barberUser?.first_name || ''} ${barberUser?.last_name || ''}`.trim()
    const customerName = `${customerUser?.first_name || ''} ${customerUser?.last_name || ''}`.trim()
    const displayName = activeRole === 'barber' ? barberName : customerName
    const otherName = activeRole === 'barber' ? customerName : barberName
    const otherRoleLabel = activeRole === 'barber' ? t('navbar.customer') : t('navbar.barber')
    const displayRole = activeRole === 'barber'
        ? (barberUser?.roles?.includes(Role.Owner) ? t(`users.role.${Role.Owner}`) : t(`users.role.${Role.Barber}`))
        : t('navbar.customer')
    const avatarSrc = activeRole === 'barber'
        ? `${IMAGE_URL}/${barberUser?.photo ?? barberUser?.barber_shop?.logo}` : undefined

    const useTransparentBg = (isLandingPage || isBookingPage) && !isLogged
    const bgColor = useTransparentBg && !scrolled ? 'transparent' : navBgSolid
    const backdropBlur = useTransparentBg && !scrolled ? 'none' : 'blur(10px)'
    const borderColorResolved = useTransparentBg ? (scrolled ? navBorderColor : 'transparent') : navBorderColor

    const publicLinks = [
        { href: '/?audience=b', label: t('landing.nav.barbers') },
        { href: '/?audience=c', label: t('landing.nav.customers') },
        { href: '/explore', label: t('landing.nav.explore') },
        { href: '/pricing', label: t('landing.nav.pricing') },
    ]

    return (
        <>
            <Flex as="header" position="sticky" top={0} zIndex={100}
                bg={bgColor} backdropFilter={backdropBlur}
                borderBottom="1px solid" borderColor={borderColorResolved}
                transition="all .25s" px={{ base: 4, md: 8 }} py={{ base: 3, md: 3.5 }}
                align="center" justify="space-between"
            >
                <HStack gap={3}>
                    <CLink asChild fontFamily={HEADING} fontWeight={700} fontSize="22px"
                        letterSpacing="-0.04em" color={logoColor} _hover={{ textDecor: 'none' }}
                    >
                        <RouterLink to="/">BARBER<Text as="span" color={RED}>MATE</Text></RouterLink>
                    </CLink>
                    {isLogged && (
                        <HStack as="nav" gap={4} display={{ base: 'none', md: 'flex' }} ml={4}>
                            {activeRole === 'barber' && (
                                <>
                                    <CLink asChild {...navLinkStyle}>
                                        <RouterLink to="/dashboard">{t('navbar.dashboard').toUpperCase()}</RouterLink>
                                    </CLink>
                                    <CLink asChild {...navLinkStyle}>
                                        <RouterLink to="/explore">{t('navbar.explore').toUpperCase()}</RouterLink>
                                    </CLink>
                                </>
                            )}
                            {activeRole === 'customer' && (
                                <>
                                    <CLink asChild {...navLinkStyle}>
                                        <RouterLink to="/customer/portal">{t('navbar.myPortal').toUpperCase()}</RouterLink>
                                    </CLink>
                                    <CLink asChild {...navLinkStyle}>
                                        <RouterLink to="/explore">{t('navbar.explore').toUpperCase()}</RouterLink>
                                    </CLink>
                                </>
                            )}
                        </HStack>
                    )}
                </HStack>

                {!isLogged ? (
                    <>
                        {!isMobile && (
                            <HStack gap={6}>
                                {publicLinks.map(l => (
                                    <CLink key={l.href} asChild {...navLinkStyle}>
                                        <RouterLink to={l.href}>{l.label.toUpperCase()}</RouterLink>
                                    </CLink>
                                ))}
                                <LangToggle />
                                <ColorModeToggle />
                                <CLink asChild {...navLinkStyle}>
                                    <RouterLink to="/login">{t('landing.nav.login').toUpperCase()}</RouterLink>
                                </CLink>
                                <Button asChild
                                    bg={BLUE} color="white" fontFamily={MONO} fontWeight={700}
                                    fontSize="11px" letterSpacing="0.2em" borderRadius="12px"
                                    px={5} py={2.5} h="auto" boxShadow={BLUE_GLOW}
                                    _hover={{ transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(0,136,255,.40)' }}
                                    transition="all .2s"
                                >
                                    <RouterLink to="/register">{t('landing.nav.cta').toUpperCase()} →</RouterLink>
                                </Button>
                            </HStack>
                        )}
                        {isMobile && (
                            <IconButton aria-label="Menu" variant="ghost" onClick={onDrawerOpen}><LuMenu /></IconButton>
                        )}
                    </>
                ) : (
                    <HStack gap={{ base: 2, md: 4 }}>
                        <Box display={{ base: 'none', sm: 'block' }}><LangToggle /></Box>
                        <ColorModeToggle />
                        <Menu.Root>
                            <Menu.Trigger asChild>
                                <Box as="button" py={2} _focus={{ boxShadow: 'none' }}>
                                    <HStack>
                                        <Avatar.Root size="sm" bg={activeRole === 'customer' ? 'purple.500' : undefined}>
                                            <Avatar.Fallback name={displayName} />
                                            {avatarSrc && <Avatar.Image src={avatarSrc} />}
                                        </Avatar.Root>
                                        <VStack display={{ base: 'none', md: 'flex' }} alignItems="flex-start" gap="1px" ml="2">
                                            <Text fontSize="sm" color={logoColor}>{displayName}</Text>
                                            <Text fontSize="xs" color={textColor}>{displayRole}</Text>
                                        </VStack>
                                        {activeRole === 'barber' && (
                                            <Box display={{ base: 'none', md: 'flex' }}><SubscriptionBadge size="sm" /></Box>
                                        )}
                                        <Box display={{ base: 'none', md: 'flex' }}><FiChevronDown /></Box>
                                    </HStack>
                                </Box>
                            </Menu.Trigger>
                            <Portal>
                                <Menu.Positioner>
                                    <Menu.Content bg={menuBg} borderColor={menuBorder} minW="220px">
                                        <Box display={{ base: 'block', md: 'none' }} px={3} py={2}>
                                            <VStack align="start" gap={1}>
                                                <Text fontWeight="semibold" fontSize="sm" lineClamp={1}>{displayName}</Text>
                                                <HStack gap={2}>
                                                    <Text fontSize="xs" color="gray.500">{displayRole}</Text>
                                                    {activeRole === 'barber' && <SubscriptionBadge size="sm" />}
                                                </HStack>
                                            </VStack>
                                        </Box>
                                        <Separator display={{ base: 'block', md: 'none' }} />
                                        {activeRole === 'barber' && (
                                            <>
                                                <Menu.Item value="dashboard" asChild>
                                                    <RouterLink to="/dashboard"><FiHome />&nbsp;{t('navbar.dashboard')}</RouterLink>
                                                </Menu.Item>
                                                <Menu.Item value="explore" asChild>
                                                    <RouterLink to="/explore"><FiHeart />&nbsp;{t('navbar.explore')}</RouterLink>
                                                </Menu.Item>
                                            </>
                                        )}
                                        {activeRole === 'customer' && (
                                            <>
                                                <Menu.Item value="portal" asChild>
                                                    <RouterLink to="/customer/portal"><FiUser />&nbsp;{t('navbar.myPortal')}</RouterLink>
                                                </Menu.Item>
                                                <Menu.Item value="waitlist" asChild>
                                                    <RouterLink to="/customer/portal#waitlist"><FiClock />&nbsp;{t('waitlist.navbar')}</RouterLink>
                                                </Menu.Item>
                                                <Menu.Item value="explore" asChild>
                                                    <RouterLink to="/explore"><FiHeart />&nbsp;{t('navbar.explore')}</RouterLink>
                                                </Menu.Item>
                                            </>
                                        )}
                                        {hasBothRoles && (
                                            <>
                                                <Separator />
                                                <Box px={3} pt={2} pb={1}>
                                                    <Text fontSize="xs" color="gray.500" lineClamp={1}>
                                                        {t('navbar.alsoSignedInAs', { role: otherRoleLabel })}
                                                    </Text>
                                                    {otherName && (
                                                        <Text fontSize="sm" fontWeight="medium" lineClamp={1}>{otherName}</Text>
                                                    )}
                                                </Box>
                                                <Menu.Item value="switchRole"
                                                    onClick={() => navigate(activeRole === 'barber' ? '/customer/portal' : '/dashboard')}
                                                >{activeRole === 'barber' ? t('navbar.switchToCustomer') : t('navbar.switchToBarber')}</Menu.Item>
                                                <Menu.Item value="signOutOther" onClick={signOutOther}>
                                                    {t('navbar.signOutRole', { role: otherRoleLabel })}
                                                </Menu.Item>
                                            </>
                                        )}
                                        {!hasBothRoles && isBarberLogged && (
                                            <Menu.Item value="loginCustomer" onClick={() => navigate('/login/customer')}>
                                                {t('navbar.loginAsCustomer')}
                                            </Menu.Item>
                                        )}
                                        {!hasBothRoles && isCustomerLogged && (
                                            <Menu.Item value="loginBarber" onClick={() => navigate('/login/barber')}>
                                                {t('navbar.loginAsBarber')}
                                            </Menu.Item>
                                        )}
                                        <Separator />
                                        <Menu.Item value="logout" onClick={signOutActive}>
                                            {hasBothRoles ? t('navbar.signOutRole', { role: displayRole }) : t('navbar.logout')}
                                        </Menu.Item>
                                    </Menu.Content>
                                </Menu.Positioner>
                            </Portal>
                        </Menu.Root>
                    </HStack>
                )}
            </Flex>

            <Drawer.Root open={drawerOpen} onOpenChange={(e) => e.open ? onDrawerOpen() : onDrawerClose()}
                placement="end" size="full">
                <Portal>
                    <Drawer.Backdrop />
                    <Drawer.Positioner>
                        <Drawer.Content bg={drawerBg}>
                            <Flex justify="space-between" align="center" p={4} px={6}>
                                <CLink asChild fontFamily={HEADING} fontWeight={700} fontSize="22px"
                                    letterSpacing="-0.04em" color="gray.900" _hover={{ textDecor: 'none' }}>
                                    <RouterLink to="/" onClick={onDrawerClose}>BARBER<Text as="span" color={RED}>MATE</Text></RouterLink>
                                </CLink>
                                <IconButton aria-label="Close" variant="ghost" onClick={onDrawerClose}><LuX /></IconButton>
                            </Flex>
                            <Drawer.Body>
                                <VStack gap={6} align="stretch" pt={8}>
                                    {publicLinks.map(l => (
                                        <CLink key={l.href} asChild fontFamily={MONO} fontSize="14px"
                                            letterSpacing="0.2em" color={logoColor}>
                                            <RouterLink to={l.href} onClick={onDrawerClose}>{l.label.toUpperCase()}</RouterLink>
                                        </CLink>
                                    ))}
                                    <CLink asChild fontFamily={MONO} fontSize="14px" letterSpacing="0.2em" color={logoColor}>
                                        <RouterLink to="/login" onClick={onDrawerClose}>{t('landing.nav.login').toUpperCase()}</RouterLink>
                                    </CLink>
                                    <HStack pt={4} gap={3}><LangToggle /><ColorModeToggle /></HStack>
                                    <Button asChild
                                        bg={BLUE} color="white" fontFamily={MONO} fontWeight={700}
                                        fontSize="13px" letterSpacing="0.2em" borderRadius="12px"
                                        px={8} py={4} h="auto" w="full" boxShadow={BLUE_GLOW}
                                    >
                                        <RouterLink to="/register" onClick={onDrawerClose}>{t('landing.nav.cta').toUpperCase()} →</RouterLink>
                                    </Button>
                                </VStack>
                            </Drawer.Body>
                        </Drawer.Content>
                    </Drawer.Positioner>
                </Portal>
            </Drawer.Root>
        </>
    )
}

// ═══════════════════════════════════════════════════════════════
// BARBER DASHBOARD TOP BAR — Barbermate-6 style (light, 72px)
// ═══════════════════════════════════════════════════════════════

const BarberTopBar = ({ barberUser, customerUser, onSidebarOpen, hasSidebar }: {
    barberUser: any; customerUser: any; onSidebarOpen: () => void; hasSidebar: boolean
}) => {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const location = useLocation()

    // Chrome is light-only on the barber dashboard (design is single-mode).
    // App.tsx forces light on /barber/* so page content stays in lockstep.
    const chromeBg = 'white'
    const chromeBorder = N[200]
    const titleColor = N[900]
    const mutedColor = N[500]
    const burgerColor = N[600]
    const menuBg = 'white'

    const isBarberLogged = useAppSelector(isBarberLoggedIn)
    const isCustomerLogged = useAppSelector(isCustomerLoggedIn)
    const hasBothRoles = isBarberLogged && isCustomerLogged

    const isOwner = barberUser?.roles?.includes(Role.Owner) ?? false
    const [viewingAs, setViewingAs] = useState<'owner' | 'barber'>(isOwner ? 'owner' : 'barber')
    useEffect(() => {
        if (!isOwner && viewingAs === 'owner') setViewingAs('barber')
    }, [isOwner, viewingAs])

    const signOutActive = () => {
        analytics.logout('barber'); analytics.clearUser(); dispatch(barberLogout())
        if (!isCustomerLogged) navigate('/'); else navigate('/customer/portal')
    }
    const signOutOther = () => {
        if (isCustomerLogged) {
            analytics.logout('customer'); dispatch(customerLogout())
        }
    }

    const barberName = barberUser?.visible_name
        || `${barberUser?.first_name || ''} ${barberUser?.last_name || ''}`.trim()
        || 'Barber'
    const customerName = `${customerUser?.first_name || ''} ${customerUser?.last_name || ''}`.trim()
    const shopName = barberUser?.barber_shop?.name || ''
    const roleLabel = viewingAs === 'owner' ? t(`users.role.${Role.Owner}`) : t(`users.role.${Role.Barber}`)
    const avatarSubtitle = shopName ? `${roleLabel} · ${shopName}` : roleLabel
    const avatarSrc = `${IMAGE_URL}/${barberUser?.photo ?? barberUser?.barber_shop?.logo}`

    const pageHeader = useBarberPageHeaderValue()
    const titleKey = topbarTitleKeyFor(location.pathname)
    const title = pageHeader.title || t(titleKey)
    const pageSubtitle = pageHeader.subtitle
    const pageAction = pageHeader.action
    const pageLeading = pageHeader.leading

    // HIDDEN_TODO #21 — multi-shop switcher hidden until multi-shop ships.
    // const shops = (barberUser?.shops as Array<{ id: string; name: string; slug: string }> | undefined)
    //     || (barberUser?.barber_shop ? [{
    //         id: barberUser.barber_shop.id || 'current',
    //         name: shopName,
    //         slug: barberUser.barber_shop.slug || '',
    //     }] : [])
    // const currentShopId = barberUser?.barber_shop?.id || 'current'

    return (
        <Flex as="header"
            position="sticky" top={0} zIndex={40}
            h="72px" bg={chromeBg} borderBottom="1px solid" borderBottomColor={chromeBorder}
            ml={{ base: 0, md: hasSidebar ? 60 : 0 }}
            w={{ base: '100%', md: hasSidebar ? 'calc(100% - var(--chakra-sizes-60))' : '100%' }}
            px="30px" align="center" justify="space-between"
        >
            <HStack gap={3} minW={0}>
                {hasSidebar && (
                    <IconButton display={{ base: 'flex', md: 'none' }}
                        onClick={onSidebarOpen} variant="ghost" aria-label="open menu"
                        color={burgerColor}
                    ><LuMenu /></IconButton>
                )}
                {pageLeading && <Box>{pageLeading}</Box>}
                <Box minW={0}>
                    <Text as="h1"
                        fontFamily={HEADING} fontWeight={700}
                        fontSize="18px" color={titleColor}
                        lineHeight="1.3"
                        truncate
                    >{title}</Text>
                    {pageSubtitle && (
                        <Text
                            fontFamily={MONO} fontSize="11px"
                            color={mutedColor} lineHeight="1.4"
                            mt="2px" truncate
                        >{pageSubtitle}</Text>
                    )}
                </Box>
            </HStack>

            <HStack gap="14px">
                <Box display={{ base: 'none', md: 'flex' }}>{pageAction}</Box>
                <ColorModeToggle />
                <Menu.Root>
                    <Menu.Trigger asChild>
                        <Box as="button" _focus={{ boxShadow: 'none' }}
                            display="flex" alignItems="center" gap="9px"
                        >
                            <Avatar.Root w="34px" h="34px" bg={N[900]} color="white" fontSize="13px" fontWeight={700}>
                                <Avatar.Fallback name={barberName} />
                                {avatarSrc && barberUser?.photo && <Avatar.Image src={avatarSrc} />}
                            </Avatar.Root>
                            <VStack display={{ base: 'none', md: 'flex' }} align="flex-start" gap="0" lineHeight="1.2">
                                <Text fontSize="13px" fontWeight={600} color={titleColor} truncate maxW="180px">{barberName}</Text>
                                <Text fontSize="11px" color={mutedColor} truncate maxW="180px">{avatarSubtitle}</Text>
                            </VStack>
                        </Box>
                    </Menu.Trigger>
                    <Portal>
                        <Menu.Positioner>
                            <Menu.Content bg={menuBg} borderColor={chromeBorder} minW="240px">
                                {/* Mobile name + role */}
                                <Box display={{ base: 'block', md: 'none' }} px={3} py={2}>
                                    <VStack align="start" gap={1}>
                                        <Text fontWeight="semibold" fontSize="sm" lineClamp={1}>{barberName}</Text>
                                        <HStack gap={2}>
                                            <Text fontSize="xs" color="gray.500">{avatarSubtitle}</Text>
                                            <SubscriptionBadge size="sm" />
                                        </HStack>
                                    </VStack>
                                </Box>
                                <Separator display={{ base: 'block', md: 'none' }} />

                                {/* Subscription badge inline (desktop) */}
                                <Box display={{ base: 'none', md: 'block' }} px={3} pt={2} pb={1}>
                                    <HStack gap={2}>
                                        <SubscriptionBadge size="sm" />
                                    </HStack>
                                </Box>

                                {/* TODO HIDDEN_TODO #20 — "Viewing as Owner / Barber" identity switcher hidden pending product decision. See docs/HIDDEN_TODO.md #20. */}
                                {/*
                                {isOwner && (
                                    <>
                                        <Separator />
                                        <Box px={3} pt={2} pb={1}>
                                            <Text fontSize="10px" fontFamily={MONO}
                                                letterSpacing="0.16em" color={N[400]}
                                                fontWeight={700} textTransform="uppercase"
                                            >{t('navbar.viewingAs')}</Text>
                                        </Box>
                                        <Menu.Item value="identity-owner"
                                            onClick={() => setViewingAs('owner')}
                                        >
                                            <HStack gap={2} flex={1}>
                                                <Text>◆ {t(`users.role.${Role.Owner}`)}</Text>
                                                {viewingAs === 'owner' && (
                                                    <Box ml="auto" color={BLUE}><FiCheck /></Box>
                                                )}
                                            </HStack>
                                        </Menu.Item>
                                        <Menu.Item value="identity-barber"
                                            onClick={() => setViewingAs('barber')}
                                        >
                                            <HStack gap={2} flex={1}>
                                                <LuScissors size={13}/>
                                                <Text>{t(`users.role.${Role.Barber}`)}</Text>
                                                {viewingAs === 'barber' && (
                                                    <Box ml="auto" color={BLUE}><FiCheck /></Box>
                                                )}
                                            </HStack>
                                        </Menu.Item>
                                    </>
                                )}
                                */}

                                {/* TODO HIDDEN_TODO #21 — barbershop switcher hidden until multi-shop lands. See docs/HIDDEN_TODO.md #21. */}
                                {/*
                                {shops.length > 0 && (
                                    <>
                                        <Separator />
                                        <Box px={3} pt={2} pb={1}>
                                            <Text fontSize="10px" fontFamily={MONO}
                                                letterSpacing="0.16em" color={N[400]}
                                                fontWeight={700} textTransform="uppercase"
                                            >{t('navbar.barbershop')}</Text>
                                        </Box>
                                        {shops.map(shop => (
                                            <Menu.Item key={shop.id} value={`shop-${shop.id}`}>
                                                <HStack gap={2} flex={1}>
                                                    <Text truncate>🏪 {shop.name}</Text>
                                                    {shop.id === currentShopId && (
                                                        <Box ml="auto" color={BLUE}><FiCheck /></Box>
                                                    )}
                                                </HStack>
                                            </Menu.Item>
                                        ))}
                                    </>
                                )}
                                */}

                                {/* Quick links */}
                                <Separator />
                                <Menu.Item value="dashboard" asChild>
                                    <RouterLink to="/dashboard"><FiHome />&nbsp;{t('navbar.dashboard')}</RouterLink>
                                </Menu.Item>
                                <Menu.Item value="settings" asChild>
                                    <RouterLink to="/barber/settings"><FiSettings />&nbsp;{t('navbar.settings')}</RouterLink>
                                </Menu.Item>

                                {/* Language inside the dropdown */}
                                <Separator />
                                <Box px={3} py={2}>
                                    <Text fontSize="10px" fontFamily={MONO}
                                        letterSpacing="0.16em" color={N[400]}
                                        fontWeight={700} textTransform="uppercase" mb={2}
                                    >{t('navbar.language')}</Text>
                                    <LangToggle />
                                </Box>

                                {/* Cross-role switcher (customer side) */}
                                {hasBothRoles && (
                                    <>
                                        <Separator />
                                        <Box px={3} pt={2} pb={1}>
                                            <Text fontSize="xs" color={N[500]} lineClamp={1}>
                                                {t('navbar.alsoSignedInAs', { role: t('navbar.customer') })}
                                            </Text>
                                            {customerName && (
                                                <Text fontSize="sm" fontWeight="medium" lineClamp={1}>{customerName}</Text>
                                            )}
                                        </Box>
                                        <Menu.Item value="switchRole"
                                            onClick={() => navigate('/customer/portal')}
                                        >{t('navbar.switchToCustomer')}</Menu.Item>
                                        <Menu.Item value="signOutOther" onClick={signOutOther}>
                                            {t('navbar.signOutRole', { role: t('navbar.customer') })}
                                        </Menu.Item>
                                    </>
                                )}
                                {!hasBothRoles && (
                                    <Menu.Item value="loginCustomer"
                                        onClick={() => navigate('/login/customer')}
                                    >{t('navbar.loginAsCustomer')}</Menu.Item>
                                )}
                                <Separator />
                                <Menu.Item value="logout" onClick={signOutActive}>
                                    {hasBothRoles
                                        ? t('navbar.signOutRole', { role: roleLabel })
                                        : t('navbar.logout')}
                                </Menu.Item>
                            </Menu.Content>
                        </Menu.Positioner>
                    </Portal>
                </Menu.Root>
            </HStack>
        </Flex>
    )
}

// ═══════════════════════════════════════════════════════════════
// NAVBAR WRAPPER
// ═══════════════════════════════════════════════════════════════

const Navbar = () => {
    const location = useLocation()
    const { open, onOpen, onClose } = useDisclosure()
    useDialogBodyCleanup(open)

    const isBarberLogged = useAppSelector(isBarberLoggedIn)
    const isCustomerLogged = useAppSelector(isCustomerLoggedIn)

    const { data: barberUser } = useGetCurrentUserQuery(undefined, { skip: !isBarberLogged })
    const { data: customerUser } = useGetCustomerMeQuery(undefined, { skip: !isCustomerLogged })

    const { showSideMenu, useBarberChrome } = useSideMenuVisibility(barberUser)

    if (useBarberChrome) {
        // Barber dashboard chrome: dashboard sidebar (or none on settings) + barber topbar
        return (
            <>
                {showSideMenu && (
                    <>
                        <SidebarContent
                            user={barberUser}
                            onClose={onClose}
                            activePath={location.pathname}
                            display={{ base: 'none', md: 'flex' }}
                        />
                        <Drawer.Root open={open}
                            placement="start"
                            onOpenChange={(e) => e.open ? onOpen() : onClose()} size="full">
                            <Portal>
                                <Drawer.Backdrop />
                                <Drawer.Positioner>
                                    <Drawer.Content>
                                        <SidebarContent
                                            user={barberUser}
                                            onClose={onClose}
                                            activePath={location.pathname}
                                        />
                                    </Drawer.Content>
                                </Drawer.Positioner>
                            </Portal>
                        </Drawer.Root>
                    </>
                )}
                <BarberTopBar
                    barberUser={barberUser}
                    customerUser={customerUser}
                    onSidebarOpen={onOpen}
                    hasSidebar={showSideMenu}
                />
            </>
        )
    }

    // Public / customer / landing chrome
    return <PublicTopBar barberUser={barberUser} customerUser={customerUser} />
}

export default Navbar
